import type { GridColumn, GridDesignColumn } from '@shared/components/kl-grid/grid.models';
import type { KlReportAggregate, KlReportColumnProperties } from './kl-report-datatable.models';

/** Aggregates in saved-code order: Sum=1, Cnt=2, Avg=3, Max=4, Min=5. */
const AGGREGATES_BY_CODE: readonly KlReportAggregate[] = ['sum', 'count', 'average', 'max', 'min'];

/** Field properties stored on a saved design column; formats/aggregates this dialog cannot produce are ignored. */
export function readSavedColumnProperties({
  format,
  aggregate,
}: GridDesignColumn): KlReportColumnProperties | undefined {
  if (format === 'string') {
    return { type: 'string' };
  }
  if (format === 'dd.MM.yyyy' || format === 'date') {
    return { type: 'date' };
  }
  const digits = format?.match(/^n([0-5])$/);
  if (!digits) {
    return undefined;
  }
  return {
    type: 'number',
    fractionDigits: Number(digits[1]),
    aggregate: typeof aggregate === 'number' ? AGGREGATES_BY_CODE[aggregate - 1] : undefined,
  };
}

/** Saved design `format`/`aggregate` for a column's field properties (nothing when it has none). */
export function toSavedColumnFormat(
  properties: KlReportColumnProperties | undefined
): Pick<GridDesignColumn, 'format' | 'aggregate'> {
  switch (properties?.type) {
    case 'number': {
      const format = `n${properties.fractionDigits}`;
      return properties.aggregate
        ? { format, aggregate: AGGREGATES_BY_CODE.indexOf(properties.aggregate) + 1 }
        : { format };
    }
    case 'date':
      return { format: 'dd.MM.yyyy' };
    case 'string':
      return { format: 'string' };
    default:
      return {};
  }
}

/** The report's own column definition with the user's field properties applied over it. */
export function applyColumnProperties(def: GridColumn, properties: KlReportColumnProperties | undefined): GridColumn {
  switch (properties?.type) {
    case 'number':
      return { ...def, format: undefined, displayFractionDigits: properties.fractionDigits, numberGrouping: true };
    case 'date':
      return { ...def, format: 'date', displayFractionDigits: undefined };
    case 'string':
      return { ...def, format: 'string', displayFractionDigits: undefined };
    default:
      return def;
  }
}

/** Report cells are formatted on every change detection, so formatters are reused. */
const reportNumberFormatters = new Map<string, Intl.NumberFormat>();

/** Number as report cells show it: fixed decimals, optional thousands grouping. */
export function formatKlReportNumber(value: number, fractionDigits: number, useGrouping: boolean): string {
  const key = `${fractionDigits}|${useGrouping}`;
  let formatter = reportNumberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping,
    });
    reportNumberFormatters.set(key, formatter);
  }
  return formatter.format(value);
}
